import { CloseButton } from '@components/close-button';
import { IconPencil } from '@components/icons/icon-pencil';
import classNames from 'classnames';

import { ModalHeaderProps } from './modal-header.type';

/**
 * ModalHeader component - New header design based on Figma specs
 *
 * Responsive design:
 * - Mobile: 18px title, 16px/20px padding
 * - Desktop (sm+): 22px title, 32px/24px padding
 *
 * Key features:
 * - Title with optional subtitle
 * - Optional primary icon on the left
 * - Optional edit button (pencil icon)
 * - Optional secondary icon
 * - Close button (X) on the right
 * - Border bottom
 *
 * @example
 * ```tsx
 * <ModalHeader
 *   title="Add tickets to group"
 *   subtitle="You're adding 4 tickets to Day 1 - August 7th."
 *   handleClose={() => setIsOpen(false)}
 * />
 * ```
 */
export const ModalHeader = ({
  titleDataTestId,
  primaryIcon,
  secondaryIcon,
  primaryIconClassName,
  primaryIconColor,
  customRightElement,
  isDisabledSecondaryIcon,
  title,
  subtitle,
  editDataTestId,
  closeDataTestId,
  textColorClass = 'text-primary',
  bordered = true,
  backgroundClassName = 'bg-primary',
  paddingClassName = 'px-4 py-5 sm:px-8 sm:py-6',
  onClickPrimaryIcon,
  onClickSecondaryIcon,
  onClickEditTitle,
  handleClose,
}: ModalHeaderProps) => {
  const PrimaryIcon = primaryIcon;
  const SecondaryIcon = secondaryIcon;

  return (
    <header
      data-testid={titleDataTestId}
      className={classNames(
        'sticky top-0 w-full z-20 flex flex-row justify-between items-start rounded-tl-lg rounded-tr-lg',
        backgroundClassName,
        paddingClassName,
        {
          'border-b border-extra-soft': bordered,
        }
      )}>
      {/* Left section: Icon + Title/Subtitle + Edit/Secondary buttons */}
      <div className='flex flex-row items-start gap-2 flex-1 min-w-0'>
        {/* Primary Icon */}
        {PrimaryIcon && (
          <button
            type='button'
            onClick={onClickPrimaryIcon}
            className={classNames(
              'flex justify-center items-center rounded-md shrink-0 max-h-7.5 max-w-7.5 sm:max-h-full sm:max-w-none',
              primaryIconClassName,
              { 'cursor-default': !onClickPrimaryIcon }
            )}
            style={{ backgroundColor: primaryIconColor }}>
            <PrimaryIcon className='w-8 h-8 text-white mix-blend-difference' />
          </button>
        )}

        {/* Title & Subtitle Container */}
        <div className='flex flex-col gap-1 flex-1 min-w-0'>
          {/* Title Row */}
          <div className='flex items-center gap-1'>
            <h3 className={classNames('text-left flex-1 min-w-0 text-headline-s sm:text-headline-m', textColorClass)}>
              {title}
            </h3>

            {/* Edit Button */}
            {onClickEditTitle && (
              <button type='button' onClick={onClickEditTitle} data-testid={editDataTestId} className='shrink-0'>
                <IconPencil className='text-primary h-4.5 w-4.5' />
              </button>
            )}

            {/* Secondary Icon */}
            {SecondaryIcon && (
              <button
                type='button'
                onClick={onClickSecondaryIcon}
                disabled={isDisabledSecondaryIcon}
                className='shrink-0'>
                <SecondaryIcon className='text-primary bg-secondary w-3.5' />
              </button>
            )}
          </div>

          {/* Subtitle */}
          {subtitle && (
            <p className='text-body-xs text-secondary leading-4 whitespace-pre-wrap wrap-break-word'>{subtitle}</p>
          )}
        </div>
      </div>

      {/* Right section: Close button or custom element */}
      <div className='shrink-0 ml-2.5'>
        {handleClose ? (
          <CloseButton id={closeDataTestId} handleClose={handleClose} dataTestId={closeDataTestId} />
        ) : null}
        {customRightElement ?? null}
      </div>
    </header>
  );
};
